import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { suppliersAPI, materialsAPI, ordersAPI, projectsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Package,
  Star,
  MapPin,
  Search,
  CheckCircle2,
  Loader2,
  Building2,
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  ClipboardList,
} from 'lucide-react';

const SuppliersPage = () => {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [activeTab, setActiveTab] = useState('materials');
  
  // Cart state
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [orderForm, setOrderForm] = useState({
    project_id: '',
    delivery_address: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedCategory || searchQuery) {
      loadMaterials();
    }
  }, [selectedCategory]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [suppliersRes, materialsRes, categoriesRes, projectsRes] = await Promise.all([
        suppliersAPI.getAll(),
        materialsAPI.getAll(),
        materialsAPI.getCategories(),
        projectsAPI.getAll(),
      ]);
      setSuppliers(suppliersRes.data.suppliers || []);
      setMaterials(materialsRes.data.materials || []);
      setCategories(categoriesRes.data.categories || []);
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadMaterials = async () => {
    try {
      const params = {};
      if (selectedCategory && selectedCategory !== 'all') params.category = selectedCategory;
      if (searchQuery) params.search = searchQuery;
      
      const response = await materialsAPI.getAll(params);
      setMaterials(response.data.materials || []);
    } catch (error) {
      console.error('Failed to load materials:', error);
    }
  };

  const filteredSuppliers = suppliers.filter((supplier) =>
    supplier.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    supplier.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredMaterials = materials.filter((material) =>
    material.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Cart functions
  const addToCart = (material) => {
    const existing = cart.find(item => item.material_id === material.material_id);
    if (existing) {
      setCart(cart.map(item => 
        item.material_id === material.material_id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...material, quantity: 1 }]);
    }
    toast.success(`Added ${material.name} to cart`);
  };

  const updateQuantity = (materialId, change) => {
    setCart(cart.map(item => {
      if (item.material_id === materialId) {
        const newQty = item.quantity + change;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (materialId) => {
    setCart(cart.filter(item => item.material_id !== materialId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price_per_unit * item.quantity), 0);

  // Check if all cart items are from same supplier
  const cartSuppliers = [...new Set(cart.map(item => item.supplier_id))];
  const canOrder = cart.length > 0 && cartSuppliers.length === 1;

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    if (!canOrder) {
      toast.error('All items must be from the same supplier');
      return;
    }
    
    setSubmitting(true);
    try {
      await ordersAPI.create({
        project_id: orderForm.project_id || null,
        items: cart.map(item => ({
          material_id: item.material_id,
          quantity: item.quantity,
        })),
        delivery_address: orderForm.delivery_address,
        notes: orderForm.notes,
      });
      toast.success('Order placed successfully!');
      setCart([]);
      setCartOpen(false);
      setOrderForm({ project_id: '', delivery_address: '', notes: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to place order';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="suppliers-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Supplier Marketplace</h1>
          <p className="text-muted-foreground mt-1">
            Browse materials and connect with trusted suppliers
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <TabsList>
              <TabsTrigger value="materials" data-testid="materials-tab">
                <Package className="w-4 h-4 mr-2" />
                Materials
              </TabsTrigger>
              <TabsTrigger value="suppliers" data-testid="suppliers-tab">
                <Building2 className="w-4 h-4 mr-2" />
                Suppliers
              </TabsTrigger>
            </TabsList>
            
            {/* Cart Button */}
            {cart.length > 0 && (
              <Button 
                onClick={() => setCartOpen(true)}
                className="bg-[#10B981] hover:bg-[#0ea572]"
                data-testid="open-cart-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Cart ({cart.length})
                <Badge variant="secondary" className="ml-2">
                  BWP {cartTotal.toLocaleString()}
                </Badge>
              </Button>
            )}
          </div>

          {/* Materials Tab */}
          <TabsContent value="materials" className="space-y-6 mt-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search materials..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-materials-input"
                />
              </div>
              
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full sm:w-56" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Materials Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredMaterials.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredMaterials.map((material) => {
                  const inCart = cart.find(item => item.material_id === material.material_id);
                  return (
                    <Card 
                      key={material.material_id} 
                      className={`border transition-colors ${inCart ? 'border-[#10B981]' : 'border-border hover:border-secondary/50'}`}
                      data-testid={`material-card-${material.material_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="w-12 h-12 bg-muted rounded-sm flex items-center justify-center mb-3">
                          <Package className="w-6 h-6 text-muted-foreground" />
                        </div>
                        
                        <h3 className="font-medium mb-1 line-clamp-1">{material.name}</h3>
                        <p className="text-xs text-muted-foreground mb-3">{material.category}</p>
                        
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-heading font-bold text-lg text-[#10B981]">
                              BWP {material.price_per_unit}
                            </p>
                            <p className="text-xs text-muted-foreground">per {material.unit}</p>
                          </div>
                          
                          {inCart ? (
                            <div className="flex items-center gap-2">
                              <Button 
                                size="icon" 
                                variant="outline" 
                                className="h-8 w-8"
                                onClick={() => updateQuantity(material.material_id, -1)}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="font-medium w-6 text-center">{inCart.quantity}</span>
                              <Button 
                                size="icon" 
                                variant="outline" 
                                className="h-8 w-8"
                                onClick={() => updateQuantity(material.material_id, 1)}
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => addToCart(material)}
                              data-testid={`add-to-cart-${material.material_id}`}
                            >
                              <ShoppingCart className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        
                        {material.available_quantity > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {material.available_quantity} in stock
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="border border-border">
                <CardContent className="p-12 text-center">
                  <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading font-bold text-lg mb-2">No materials found</h3>
                  <p className="text-muted-foreground">
                    Try adjusting your search or filters
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Suppliers Tab */}
          <TabsContent value="suppliers" className="space-y-6 mt-6">
            {/* Search */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search suppliers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-suppliers-input"
              />
            </div>

            {/* Suppliers Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredSuppliers.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredSuppliers.map((supplier) => (
                  <Card 
                    key={supplier.supplier_id} 
                    className="border border-border hover:border-secondary/50 transition-colors"
                    data-testid={`supplier-card-${supplier.supplier_id}`}
                  >
                    <CardContent className="p-6">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-14 h-14 bg-[#0F2640] rounded-sm flex items-center justify-center">
                          <Building2 className="w-7 h-7 text-white" />
                        </div>
                        {supplier.verified && (
                          <span className="bg-emerald-100 text-emerald-700 px-2 py-1 text-xs font-medium rounded flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Verified
                          </span>
                        )}
                      </div>

                      <h3 className="font-heading font-bold text-lg mb-1">
                        {supplier.business_name}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        {supplier.business_type}
                      </p>

                      {supplier.description && (
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                          {supplier.description}
                        </p>
                      )}

                      {/* Details */}
                      <div className="space-y-2 text-sm text-muted-foreground mb-4">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{supplier.address}, {supplier.city}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Star className="w-4 h-4 text-amber-400 fill-current" />
                          <span className="font-medium text-foreground">
                            {supplier.rating?.toFixed(1)}
                          </span>
                          <span>({supplier.total_orders} orders)</span>
                        </div>
                      </div>

                      <Button className="w-full" variant="outline">
                        View Products
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border border-border">
                <CardContent className="p-12 text-center">
                  <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading font-bold text-lg mb-2">No suppliers found</h3>
                  <p className="text-muted-foreground">
                    Try adjusting your search
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Cart Modal */}
        <Dialog open={cartOpen} onOpenChange={setCartOpen}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Your Cart ({cart.length} items)
              </DialogTitle>
            </DialogHeader>
            
            {cart.length > 0 ? (
              <div className="space-y-4 mt-4">
                {/* Cart Items */}
                <div className="space-y-3">
                  {cart.map((item) => (
                    <div 
                      key={item.material_id} 
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-sm"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.name}</p>
                        <p className="text-xs text-muted-foreground">
                          BWP {item.price_per_unit} × {item.quantity} = BWP {(item.price_per_unit * item.quantity).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="icon" 
                          variant="outline" 
                          className="h-7 w-7"
                          onClick={() => updateQuantity(item.material_id, -1)}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="font-medium w-6 text-center text-sm">{item.quantity}</span>
                        <Button 
                          size="icon" 
                          variant="outline" 
                          className="h-7 w-7"
                          onClick={() => updateQuantity(item.material_id, 1)}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-7 w-7 text-destructive"
                          onClick={() => removeFromCart(item.material_id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Warning if multiple suppliers */}
                {cartSuppliers.length > 1 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
                    <p className="text-sm text-amber-800">
                      Items must be from the same supplier. Please remove items from different suppliers.
                    </p>
                  </div>
                )}

                {/* Total */}
                <div className="flex items-center justify-between p-3 bg-[#0F2640] text-white rounded-sm">
                  <span className="font-medium">Total</span>
                  <span className="font-heading font-bold text-lg">BWP {cartTotal.toLocaleString()}</span>
                </div>

                {/* Order Form */}
                <form onSubmit={handleOrderSubmit} className="space-y-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label>Link to Project (Optional)</Label>
                    <Select 
                      value={orderForm.project_id} 
                      onValueChange={(v) => setOrderForm({...orderForm, project_id: v})}
                    >
                      <SelectTrigger data-testid="order-project-select">
                        <SelectValue placeholder="Select a project" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No project</SelectItem>
                        {projects.map((project) => (
                          <SelectItem key={project.project_id} value={project.project_id}>
                            {project.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Delivery Address</Label>
                    <Input
                      value={orderForm.delivery_address}
                      onChange={(e) => setOrderForm({...orderForm, delivery_address: e.target.value})}
                      placeholder="Enter delivery address"
                      data-testid="order-address-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={orderForm.notes}
                      onChange={(e) => setOrderForm({...orderForm, notes: e.target.value})}
                      placeholder="Any special instructions..."
                      rows={2}
                      data-testid="order-notes-input"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setCartOpen(false)}>
                      Continue Shopping
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={submitting || !canOrder}
                      className="bg-[#10B981] hover:bg-[#0ea572]"
                      data-testid="place-order-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Placing Order...
                        </>
                      ) : (
                        <>
                          <ClipboardList className="w-4 h-4 mr-2" />
                          Place Order
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="text-center py-8">
                <ShoppingCart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Your cart is empty</p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default SuppliersPage;
